import { TableView } from 'api/models/TableView';
import { COMPANY_NAME_KEY } from 'common/constants/analyst-dashboards';
import { valuationsColumns } from 'dashboard409a/pages/Workboard/common/colConfig';
import { DEFAULT_VIEW_ID } from 'dashboard409a/pages/Workboard/common/constants';
import {
  ValuationView,
  WORKBOARD_META_COLUMNS,
  WorkboardTableDefinition,
} from '../types/workboardTableViews/types';

const DEFAULT_COLUMN_ORDER = valuationsColumns.map(col => col.id);

const EMPTY_TABLE_DEFINITION: WorkboardTableDefinition = {
  columns: {},
  sortKeys: [],
  groupBy: 'companies',
  filters: [],
  filterExclusions: [],
};

export const normalizeTableDefinition = (
  definition?: WorkboardTableDefinition | Record<string, unknown> | null
): WorkboardTableDefinition => {
  if (!definition || typeof definition !== 'object') {
    return { ...EMPTY_TABLE_DEFINITION };
  }

  const def = definition as Record<string, unknown>;
  return {
    columns: (def.columns as Record<number, string>) ?? {},
    sortKeys: (def.sortKeys as WorkboardTableDefinition['sortKeys']) ?? [],
    groupBy: (def.groupBy as WorkboardTableDefinition['groupBy']) ?? 'companies',
    filters: (def.filters as WorkboardTableDefinition['filters']) ?? [],
    filterExclusions: (def.filterExclusions as string[]) ?? [],
    columnWidths: (def.columnWidths as WorkboardTableDefinition['columnWidths']) ?? undefined,
  };
};

const resolveColumnOrder = (columnOrder?: string[]): string[] => {
  if (!columnOrder || columnOrder.length === 0) {
    return [...DEFAULT_COLUMN_ORDER];
  }
  const known = new Set(DEFAULT_COLUMN_ORDER);
  const ordered = columnOrder.filter(id => known.has(id));
  const missing = DEFAULT_COLUMN_ORDER.filter(id => !ordered.includes(id));
  return [...ordered, ...missing];
};

const buildColumnOrder = (definition: WorkboardTableDefinition): string[] => {
  const orderedFromDefinition = Object.entries(definition.columns)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, col]) => col);
  const hiddenColumnIds = definition.filterExclusions ?? [];
  return resolveColumnOrder([...orderedFromDefinition, ...hiddenColumnIds]);
};

const buildColumnVisibility = (
  definition: WorkboardTableDefinition
): Record<string, boolean> => {
  const visibleColumnIds = new Set(Object.values(definition.columns));
  const hiddenColumnIds = new Set(definition.filterExclusions ?? []);
  const columnVisibility: Record<string, boolean> = {};

  DEFAULT_COLUMN_ORDER.forEach(columnId => {
    if (columnId === COMPANY_NAME_KEY) {
      return;
    }
    if (hiddenColumnIds.has(columnId)) {
      columnVisibility[columnId] = false;
      return;
    }
    if (visibleColumnIds.size > 0) {
      columnVisibility[columnId] = visibleColumnIds.has(columnId);
    }
  });

  return columnVisibility;
};

const buildColumnsFromVisibility = (
  columnVisibility: Record<string, boolean> = {},
  columnOrder?: string[]
): Pick<WorkboardTableDefinition, 'columns' | 'filterExclusions'> => {
  const order = resolveColumnOrder(columnOrder);
  const visibleColumns = order.filter(columnId => {
    if (columnId === COMPANY_NAME_KEY) {
      return true;
    }
    return columnVisibility[columnId] !== false;
  });

  const columns = visibleColumns.reduce<Record<number, string>>((acc, columnId, index) => {
    acc[index] = columnId;
    return acc;
  }, {});

  const filterExclusions = order.filter(
    columnId =>
      columnId !== COMPANY_NAME_KEY && columnVisibility[columnId] === false
  );

  return { columns, filterExclusions };
};

const valuationViewToTableDefinition = (
  view: ValuationView,
  sectionId?: string
): WorkboardTableDefinition => {
  const filters: WorkboardTableDefinition['filters'] = [];

  if (sectionId) {
    filters.push([WORKBOARD_META_COLUMNS.SECTION, ['=', sectionId]]);
  }

  if (view.groupByDeals) {
    filters.push([WORKBOARD_META_COLUMNS.GROUP_BY_DEALS, ['=', 'true']]);
  }

  const { columns, filterExclusions } = buildColumnsFromVisibility(
    view.columnVisibility,
    view.columnOrder
  );

  return {
    columns,
    sortKeys: [],
    groupBy: 'companies',
    filters,
    filterExclusions,
  };
};

const decodeGroupByDeals = (filters: WorkboardTableDefinition['filters']): boolean =>
  filters.some(
    ([col, [, value]]) => col === WORKBOARD_META_COLUMNS.GROUP_BY_DEALS && String(value) === 'true'
  );

export const getValuationViewSectionId = (definition: WorkboardTableDefinition): string | undefined => {
  const sectionFilter = definition.filters.find(([col]) => col === WORKBOARD_META_COLUMNS.SECTION);
  if (!sectionFilter) {
    return undefined;
  }
  return String(sectionFilter[1][1]);
};

export const getDefaultValuationView = (): ValuationView => ({
  id: DEFAULT_VIEW_ID,
  name: 'Default',
  columnVisibility: {},
  columnOrder: [...DEFAULT_COLUMN_ORDER],
  groupByDeals: false,
});

export const tableDefinitionToValuationView = (
  tableView: TableView,
  definition: WorkboardTableDefinition
): ValuationView => ({
  id: tableView.id != null ? String(tableView.id) : DEFAULT_VIEW_ID,
  name: tableView.name,
  columnVisibility: buildColumnVisibility(definition),
  columnOrder: buildColumnOrder(definition),
  groupByDeals: decodeGroupByDeals(definition.filters),
  visibility: tableView.visibility,
  createdBy: tableView.created_by,
});

export const fromValuationTableView = (tableView: TableView): ValuationView => {
  const definition = normalizeTableDefinition(
    tableView.table_definition as WorkboardTableDefinition | undefined
  );
  return tableDefinitionToValuationView(tableView, definition);
};

export const toValuationTableViewCreatePayload = (
  view: ValuationView,
  sectionId?: string
): TableView => ({
  name: view.name,
  visibility: (view.visibility as TableView.visibility | undefined) ?? TableView.visibility.PRIVATE,
  table_definition: valuationViewToTableDefinition(view, sectionId),
  is_default: false,
});

export const toValuationTableViewUpdatePayload = (
  view: ValuationView,
  sectionId?: string
): Partial<TableView> => ({
  name: view.name,
  visibility: view.visibility as TableView.visibility | undefined,
  table_definition: valuationViewToTableDefinition(view, sectionId),
});

export const canEditValuationTableView = (
  view: ValuationView,
  currentUserId: number | undefined,
  isStaff: boolean
): boolean => {
  if (view.id === DEFAULT_VIEW_ID) {
    return false;
  }

  if (view.visibility === TableView.visibility.PUBLIC) {
    return isStaff || view.createdBy === currentUserId;
  }

  return view.createdBy == null || view.createdBy === currentUserId;
};

export { DEFAULT_COLUMN_ORDER, resolveColumnOrder };
